import React, { useState, useEffect } from 'react';
import './TraineeList.css';
import { supabase } from '../../../lib/supabaseClient';
import { DailyHours, StudentProfile } from '../../../lib/database.types';
import { useAuth } from '../../../lib/AuthProvider';
import WeeklyStudentsPanel from './WeeklyStudentsPanel';

interface WeeklyAttendanceSummary {
  id: string;
  student_id: string;
  week_number: number;
  scheduled_days: number;
  days_present: number;
  days_absent: number;
  noclass_days: number;
  avg_hours_per_day: number;
  late_minutes: number;
  created_at: string;
  updated_at: string;
}

interface WeeklyTrainee {
  name: string;
  department: string;
  weeklyHours: number;
  status: 'At Risk' | 'Not At Risk';
  riskScore?: number;
  attendanceRate?: number;
  absentRate?: number;
  holidayRatio?: number;
}

interface GeneralTrainee {
  name: string;
  department: string;
  totalHours: number;
  requiredHours: number;
  progressRate: number;
  studentId: string;
  supervisor: string;
  userId: string;
}

interface TraineeProfile {
  name: string;
  department: string;
  studentId: string;
  supervisor: string;
  totalProgress: number;
  hoursCompleted: number;
  reportsSubmitted: number;
  requirementsCompleted: number;
  lastActivity: string;
  hoursToday: number;
  recentActivities: {
    timeIn: string;
    timeOut: string;
    date: string;
  }[];
  totalHoursCompleted: number;
  amHours: number;
  pmHours: number;
}

const TraineeList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'weekly' | 'general'>('weekly');
  const [showModal, setShowModal] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState<TraineeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [weeklyTrainees, setWeeklyTrainees] = useState<WeeklyTrainee[]>([]);
  const [generalTrainees, setGeneralTrainees] = useState<GeneralTrainee[]>([]);
  
  // Get current user from AuthProvider to exclude from student profile fetch
  const { user } = useAuth();

  // Function to call the risk prediction API
  const predictRisk = async (weeklyData: WeeklyAttendanceSummary): Promise<{risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}> => {
    try {
      const response = await fetch('http://localhost:5000/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weekly_data: {
            scheduled_days: weeklyData.scheduled_days,
            days_present: weeklyData.days_present,
            days_absent: weeklyData.days_absent,
            noclass_days: weeklyData.noclass_days,
            avg_hours_per_day: weeklyData.avg_hours_per_day,
            late_minutes: weeklyData.late_minutes
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error calling risk prediction API:', error);
      // Return default values if API call fails
      return {
        risk_label: '0',
        attendance_rate: 0,
        absent_rate: 0,
        holiday_ratio: 0
      };
    }
  };

  // Fetch trainee data from database
  useEffect(() => {
    const fetchTraineeData = async () => {
      try {
        setLoading(true);
        // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
        console.log('Fetching trainee data...');
        console.log('Current user info:', { 
          userId: user?.id, 
          userType: user ? ('role' in user ? user.role : (user as any)?.user_role) : null,
          hasUser: !!user 
        });
        
        // First, let's check if there are any profiles at all
        console.log('Checking if profiles table exists and has data...');
        const { data: allProfiles, error: allProfilesError } = await supabase
          .from('student_profile')
          .select('*')
          .limit(5);
        
        console.log('All profiles check:', { allProfiles, allProfilesError });
        console.log('Number of all profiles found:', allProfiles?.length || 0);
        
        // Also check supervisor_profiles to see if the user is there
        const { data: supervisorProfiles, error: supervisorError } = await supabase
          .from('supervisor_profiles')
          .select('*')
          .limit(5);
        
        console.log('Supervisor profiles check:', { supervisorProfiles, supervisorError });
        console.log('Number of supervisor profiles found:', supervisorProfiles?.length || 0);
        
        // Check if there are any users in auth.users (this might not work due to RLS)
        try {
          const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
          console.log('Auth users check:', { authUsers: authUsers?.users?.length || 0, authError });
        } catch (error) {
          console.log('Cannot check auth users (expected for non-admin):', error);
        }

        // Fetch all student profiles (OJT coordinators are in supervisor_profiles, so no need to exclude)
        console.log('Attempting to fetch student profiles from database...');
        const { data: profiles, error: profilesError } = await supabase
          .from('student_profile')
          .select('*');

        console.log('Student profiles query result:', { profiles, profilesError });
        console.log('Number of student profiles found:', profiles?.length || 0);
        
        // Check if this is an RLS permission issue
     

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          setLoading(false);
          return;
        }

        if (!profiles || profiles.length === 0) {
          console.log('No student profiles found in database');
          console.log('Students need to register first through the registration system');
          setGeneralTrainees([]);
          setWeeklyTrainees([]);
          setLoading(false);
          return;
        }

        // Fetch daily hours for all students
        const { data: dailyHours, error: hoursError } = await supabase
          .from('daily_hours')
          .select('*');

        console.log('Daily hours query result:', { dailyHours, hoursError });

        if (hoursError) {
          console.error('Error fetching daily hours:', hoursError);
          // Continue without daily hours data
        }

        // Fetch weekly attendance summary for all students
        const { data: weeklyAttendance, error: weeklyError } = await supabase
          .from('weekly_attendance_summary')
          .select('*')
          .order('week_number', { ascending: false });

        console.log('Weekly attendance query result:', { weeklyAttendance, weeklyError });

        if (weeklyError) {
          console.error('Error fetching weekly attendance:', weeklyError);
          // Continue without weekly attendance data
        }

        // Calculate total hours for each student
        const studentHoursMap = new Map<string, number>();
        if (dailyHours) {
          dailyHours.forEach((hour: DailyHours) => {
            const currentTotal = studentHoursMap.get(hour.user_id) || 0;
            studentHoursMap.set(hour.user_id, currentTotal + hour.total_hours);
          });
        }

        // Create general trainees list
        const generalTraineesData: GeneralTrainee[] = profiles.map((profile: StudentProfile, index: number) => {
          const totalHours = studentHoursMap.get(profile.id) || 0;
          const requiredHours = 500; // Standard requirement
          const progressRate = Math.round((totalHours / requiredHours) * 100);

          return {
            name: `${index + 1}.) ${profile.first_name} ${profile.last_name}`,
            department: profile.department,
            totalHours: Math.round(totalHours * 100) / 100,
            requiredHours,
            progressRate: Math.min(progressRate, 100),
            studentId: profile.student_id,
            supervisor: profile.supervisor,
            userId: profile.id
          };
        });

        console.log('General trainees data:', generalTraineesData);
        setGeneralTrainees(generalTraineesData);

        // Create weekly trainees list with risk prediction
        const weeklyTraineesData: WeeklyTrainee[] = await Promise.all(
          profiles.map(async (profile: StudentProfile) => {
            // Find the most recent weekly attendance data for this student
            const studentWeeklyData = weeklyAttendance?.find(
              (attendance: WeeklyAttendanceSummary) => attendance.student_id === profile.student_id
            );

            if (studentWeeklyData) {
              // Get risk prediction
              const riskPrediction = await predictRisk(studentWeeklyData);
              
              // Calculate weekly hours from avg_hours_per_day * days_present
              const weeklyHours = (studentWeeklyData as WeeklyAttendanceSummary).avg_hours_per_day * (studentWeeklyData as WeeklyAttendanceSummary).days_present;
              
              return {
                name: `${profile.first_name} ${profile.last_name}`,
                department: profile.department,
                weeklyHours: Math.round(weeklyHours * 10) / 10,
                status: riskPrediction.risk_label === '1' ? 'At Risk' : 'Not At Risk',
                riskScore: parseFloat(riskPrediction.risk_label),
                attendanceRate: riskPrediction.attendance_rate,
                absentRate: riskPrediction.absent_rate,
                holidayRatio: riskPrediction.holiday_ratio
              };
            } else {
              // Fallback to mock data if no weekly attendance data
              const weeklyHours = Math.random() * 40 + 20;
              return {
                name: `${profile.first_name} ${profile.last_name}`,
                department: profile.department,
                weeklyHours: Math.round(weeklyHours * 10) / 10,
                status: weeklyHours < 30 ? 'At Risk' : 'Not At Risk'
              };
            }
          })
        );

        console.log('Weekly trainees data:', weeklyTraineesData);
        setWeeklyTrainees(weeklyTraineesData);

      } catch (error) {
        console.error('Error fetching trainee data:', error);
      } finally {
        setLoading(false);
        // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
      }
    };

    fetchTraineeData();
  }, [user?.id]); // Add user.id as dependency to re-run when user is available

  const viewTraineeDetails = async (trainee: WeeklyTrainee | GeneralTrainee) => {
    try {
      // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
      // Get user ID from trainee data
      const userId = 'userId' in trainee ? trainee.userId : null;
      if (!userId) {
        console.error('User ID not found for trainee');
        return;
      }


      // Fetch detailed profile information
      const { data: profile, error: profileError } = await supabase
        .from('student_profile')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Fetch daily hours for this student
      const { data: dailyHours, error: hoursError } = await supabase
        .from('daily_hours')
        .select('*')
        .eq('user_id', userId)
        .order('check_date', { ascending: false });

      if (hoursError) {
        console.error('Error fetching daily hours:', hoursError);
        return;
      }

      // Fetch recent check-ins for activity history
      const { data: checkIns, error: checkInsError } = await supabase
        .from('check_ins')
        .select('*')
        .eq('user_id', userId)
        .order('check_time', { ascending: false })
        .limit(5);

      if (checkInsError) {
        console.error('Error fetching check-ins:', checkInsError);
        return;
      }

      // Calculate total hours
      const totalHoursCompleted = dailyHours?.reduce((sum, day: DailyHours) => sum + day.total_hours, 0) || 0;
      
      // Get today's hours
      const today = new Date().toISOString().split('T')[0];
      const todayHours = dailyHours?.find((day: DailyHours) => day.check_date === today) as DailyHours | undefined;
      const hoursToday = todayHours?.total_hours || 0;

      // Get last activity date
      const lastActivity = (dailyHours as DailyHours[])?.[0]?.check_date || 'No activity';

      // Create recent activities from check-ins
      const recentActivities = checkIns?.map((checkIn: any) => {
        const checkTime = new Date(checkIn.check_time);
        const timeString = checkTime.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        const dateString = checkTime.toLocaleDateString();
        
        return {
          timeIn: checkIn.check_type.includes('check_in') ? `AM ${timeString}` : `PM ${timeString}`,
          timeOut: checkIn.check_type.includes('check_out') ? `PM ${timeString}` : `AM ${timeString}`,
          date: dateString
        };
      }) || [];

      // Calculate progress
      const requiredHours = 500;
      const totalProgress = Math.round((totalHoursCompleted / requiredHours) * 100);

      const traineeProfile: TraineeProfile = {
        name: trainee.name.replace(/^\d+\.\)\s/, ''), // Remove numbering if present
        department: trainee.department,
        studentId: (profile as StudentProfile).student_id,
        supervisor: (profile as StudentProfile).supervisor,
        totalProgress: Math.min(totalProgress, 100),
        hoursCompleted: Math.round(totalHoursCompleted * 100) / 100,
        reportsSubmitted: 5, // Static as requested
        requirementsCompleted: 3, // Static as requested
        lastActivity: lastActivity,
        hoursToday: Math.round(hoursToday * 100) / 100,
        recentActivities: recentActivities.slice(0, 3), // Limit to 3 most recent
        totalHoursCompleted: Math.round(totalHoursCompleted * 100) / 100,
        amHours: todayHours?.am_hours || 0,
        pmHours: todayHours?.pm_hours || 0
      };
      
      setSelectedTrainee(traineeProfile);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching trainee details:', error);
    } finally {
      // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
    }
  };

  // Close the modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedTrainee(null);
  };

  if (loading) {
    return (
      <div className="trainee-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading trainee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trainee-list">
      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly
        </button>
        <button 
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button 
          className="refresh-button"
          onClick={() => window.location.reload()}
          style={{ marginLeft: 'auto', padding: '8px 16px', backgroundColor: '#0066cc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          Refresh Data
        </button>
      </div>
      
      {activeTab === 'weekly' ? (
        <WeeklyStudentsPanel 
          trainees={weeklyTrainees} 
          loading={false}
          filterAtRisk={false}
        />
      ) : (
        <div className="trainee-table general-view">
          {generalTrainees.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Trainee</th>
                  <th>Department</th>
                  <th>Total Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {generalTrainees.map((trainee, index) => (
                  <tr key={index}>
                    <td>{trainee.name}</td>
                    <td>{trainee.department}</td>
                    <td>
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${trainee.progressRate}%` }} 
                          />
                        </div>
                        <div className="progress-text">
                          <span>Progress Rate: {trainee.progressRate}%</span>
                          <span>{trainee.totalHours}/{trainee.requiredHours} hours</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <button 
                        className="view-button" 
                        onClick={() => viewTraineeDetails(trainee)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data-message">
              <h3>No trainee data available</h3>
              <p>No students have registered yet.</p>
              <p>Students need to register through the student registration system first.</p>
              <p>Once students register and start checking in/out, their data will appear here.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Trainee Profile Modal */}
      {showModal && selectedTrainee && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainee-profile">
              <div className="profile-header">
                <h2>Trainee Profile</h2>
                <button className="close-button" onClick={closeModal}>&times;</button>
              </div>
              
              <div className="profile-info-grid">
                <div className="info-field">
                  <label>Name</label>
                  <div className="info-value">{selectedTrainee.name}</div>
                </div>
                <div className="info-field">
                  <label>Supervisor</label>
                  <div className="info-value">{selectedTrainee.supervisor}</div>
                </div>
                <div className="info-field">
                  <label>Student ID</label>
                  <div className="info-value">{selectedTrainee.studentId}</div>
                </div>
                <div className="info-field">
                  <label>Department</label>
                  <div className="info-value">{selectedTrainee.department}</div>
                </div>
              </div>
              
              <div className="profile-stats">
                <div className="progress-circle">
                  <svg viewBox="0 0 36 36" className="progress-ring">
                    <path className="progress-ring-bg"
                      d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path className="progress-ring-fill"
                      strokeDasharray={`${selectedTrainee.totalProgress}, 100`}
                      d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" className="progress-text">{selectedTrainee.totalProgress}%</text>
                  </svg>
                  <div className="progress-label">Total Progress</div>
                </div>
                
                <div className="stat-bars">
                  <div className="stat-item">
                    <label>Hours Completed</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: `${Math.min(selectedTrainee.totalProgress, 100)}%`}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.totalHoursCompleted} / 500 hours</div>
                  </div>
                  <div className="stat-item">
                    <label>Reports Submitted</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: '75%'}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.reportsSubmitted} reports</div>
                  </div>
                  <div className="stat-item">
                    <label>Requirements Completed</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: '40%'}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.requirementsCompleted} requirements</div>
                  </div>
                </div>
                
                <div className="activity-metrics">
                  <div className="metric-box">
                    <span className="metric-label">Last Activity</span>
                    <span className="metric-value">{selectedTrainee.lastActivity}</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Hours Today</span>
                    <span className="metric-value">{selectedTrainee.hoursToday} hours</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">AM Hours</span>
                    <span className="metric-value">{selectedTrainee.amHours} hours</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">PM Hours</span>
                    <span className="metric-value">{selectedTrainee.pmHours} hours</span>
                  </div>
                </div>
              </div>
              
              <div className="recent-activities">
                <h3>Recent Activities</h3>
                {selectedTrainee.recentActivities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="time-record">
                      <div className="time-in">
                        <span className="time-label">Time In : {activity.timeIn.split(' ')[0]}</span>
                        <span className="time-value">{activity.timeIn.split(' ')[1]}</span>
                      </div>
                      <div className="time-out">
                        <span className="time-label">Time Out : {activity.timeOut.split(' ')[0]}</span>
                        <span className="time-value">{activity.timeOut.split(' ')[1]}</span>
                      </div>
                    </div>
                    <div className="activity-date">{activity.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TraineeList;
